import {
  LinkIcon,
  StarIcon,
  TagIcon,
  TicketIcon,
} from "@heroicons/react/outline";
import { HttpError, useOne, useTranslation } from "@refinedev/core";
import { Calendar, Location } from "components/customIcons";
import { IEventDetail } from "interfaces";
import { get } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  CURRENCIES,
  PROVINCE,
  SPECIAL_TYPES,
  TIMEZONES,
} from "utills/constants";
import { formatNumber } from "utills/helpers";
import CONFIG from "app-config/config.json";
import { endpointService } from "components/shared/services/endpointService";
export const EventInfo = ({
  eventDetailData,
}: {
  eventDetailData?: IEventDetail;
}) => {
  const { translate } = useTranslation();
  const { data: categoryData } = useOne<any[], HttpError>({
    resource: endpointService.getEventEndpoint("categories"),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const provinceIds = PROVINCE.map((proinvce) => ({
    value: proinvce.value,
    label: proinvce.name_en,
  }));

  const categoryIds = (categoryData?.data ?? []).map((category) => ({
    value: category.id,
    label: category.name_en,
  }));

  const timezoneArr = Object.keys(TIMEZONES as any).map(
    (key: string, index: number) => ({
      value: key,
      label: get(TIMEZONES, `${key}.value`, ""),
    })
  );

  return (
    <div className="box-container p-4 sm:p-6">
      <div className="flex flex-col">
        <div className="relative w-full overflow-hidden">
          <img
            src={eventDetailData?.banner_url ?? ""}
            alt={`${eventDetailData?.banner_url} Banner`}
            className="rounded-lg"
          />
        </div>
        <div className="flex flex-col justify-center items-start mt-4">
          <div className="w-full flex flex-row items-start gap-x-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <Calendar />
              {translate("eventSetup.eventInfo.addEventInfo.eventDateTime")}:
            </div>
            <p>
              {eventDetailData?.start_date
                ? moment(eventDetailData?.start_date).format("HH:mm DD/MM/YYYY")
                : ""}{" "}
              -{" "}
              {eventDetailData?.end_date
                ? moment(eventDetailData?.end_date).format("HH:mm DD/MM/YYYY")
                : ""}{" "}
              <span className="mx-2">•</span>
              {
                timezoneArr.find(
                  (timezone) => timezone.value === eventDetailData?.timezone
                )?.label
              }{" "}
              <span className="mx-2">•</span>{" "}
              {
                CURRENCIES.find(
                  (currency) => currency.value === eventDetailData?.currency
                )?.label
              }
            </p>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <Location />
              {translate("eventSetup.eventInfo.addEventInfo.eventVenue")}:
            </div>
            <p>
              {
                provinceIds.find(
                  (province) => province.value === eventDetailData?.venue
                )?.label
              }
              {eventDetailData?.venue ? "," : ""} {eventDetailData?.country}
            </p>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <LinkIcon className="w-4 h-4" />
              {translate("eventSetup.eventInfo.addEventInfo.eventLink")}:
            </div>
            <Link
              to={`${CONFIG.DOMAIN}/event/${eventDetailData?.event_slug}`}
              target="_blank"
              className="hover:underline text-brand-500"
              style={{ wordBreak: "break-word" }}
            >
              {CONFIG.DOMAIN}/event/{eventDetailData?.event_slug}
            </Link>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <StarIcon className="w-4 h-4" />
              {translate("eventSetup.eventInfo.addEventInfo.eventCategories")}:
            </div>
            <div
              className="flex flex-row items-center flex-wrap gap-x-2"
              style={{ wordBreak: "break-word" }}
            >
              {categoryIds
                .filter((category) =>
                  (eventDetailData?.event_category_ids ?? []).includes(
                    category.value
                  )
                )
                .map((category, index) => (
                  <p key={category.value}>
                    {index !== 0 ? <span className="mr-2">•</span> : ""}
                    {category.label}
                  </p>
                ))}
            </div>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <TicketIcon className="w-4 h-4" />
              {translate("eventSetup.eventInfo.addEventInfo.minPrice")}
            </div>
            <p>{formatNumber(eventDetailData?.min_price ?? 0)}</p>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <TagIcon className="w-4 h-4" />
              {translate("eventSetup.eventInfo.addEventInfo.eventTag")}:
            </div>
            <p>
              {
                SPECIAL_TYPES.find(
                  (type) => type.value === eventDetailData?.special_type
                )?.label
              }
            </p>
          </div>
          <div className="w-full flex flex-row items-start gap-x-2 mt-2">
            <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
              <TicketIcon className="w-4 h-4" />
              {translate("eventSetup.eventInfo.addEventInfo.totalTicketForSale")}
            </div>
            <p>{formatNumber(eventDetailData?.selling_max_qty ?? 0)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
